/**
 * A report page as it will actually be issued.
 *
 * The preview is where ADR 0007's boundary becomes visible rather than merely stated. The
 * accent draws the cover band and the section rule — and nothing else. Body text, table
 * structure, section order and the four finding badges stay Complao's, drawn straight from
 * the design tokens.
 *
 * The badges are rendered here on purpose. A certification body whose brand is red must be
 * able to see, before it submits, that a major nonconformity does not become house
 * styling — and that the badge colours did not move when its accent did.
 *
 * `ruleColour` is the server's resolution, not the raw accent: where the accent fails
 * contrast this arrives as Deep Forest, so the preview shows the fallback the document
 * will really use rather than the colour the applicant typed.
 */

const OUTCOMES = [
  { key: "conforming", label: "Conforming" },
  { key: "observation", label: "Observation" },
  { key: "minor-nc", label: "Minor NC" },
  { key: "major-nc", label: "Major NC" },
] as const;

export function DocumentPreview({
  legalName,
  markPreview,
  ruleColour,
}: {
  legalName: string;
  markPreview: string | null;
  /** Resolved by the server — the accent, or Deep Forest where it failed contrast. */
  ruleColour: string | null;
}) {
  // The one place a colour is not a token: it is the certification body's, resolved by
  // the server, and it reaches exactly two rules.
  const rule = ruleColour ?? undefined;

  return (
    <div className="preview">
      <div className="preview__cover">
        {markPreview ? (
          // On a light plate rather than inverted. The prototype inverts, which suits its
          // sample marks — transparent SVGs with dark strokes — but turns any mark with an
          // opaque background into a solid white rectangle. A plate reads correctly
          // whatever a certification body uploads.
          <span className="preview__markplate">
            <img className="preview__mark" src={markPreview} alt="" />
          </span>
        ) : (
          <span className="preview__name">{legalName || "Your certification body"}</span>
        )}
        <span className="preview__reference">Stage 1 Audit Report · AUD-2026-0001</span>
      </div>

      <div className="preview__band" style={{ background: rule }} />

      <div className="preview__body">
        <div className="preview__section">1 · Audit details</div>
        <hr className="preview__rule" style={{ background: rule }} />
        <div className="preview__copy">
          Body text, tables and typography stay Complao&rsquo;s.
        </div>

        <div className="preview__badges">
          {OUTCOMES.map((outcome) => (
            <span className={`badge badge--${outcome.key}`} key={outcome.key}>
              {outcome.label}
            </span>
          ))}
        </div>
        <div className="preview__note">
          Finding colours are fixed by Complao and are not affected by your accent.
        </div>
      </div>

      <div className="preview__footer">Powered by Complao</div>
    </div>
  );
}
